import {
  Flex,
  Button,
  Image,
  Text,
  useBreakpoint,
  Box,
} from "@chakra-ui/react";
import { ContentSectionCustomCard } from "../../types";
import { useIsSidebarOpen } from "../../hooks/useIsSidebarOpen";
import { DEFAULT_SIDEBAR_WIDTH } from "../../screens/root/constants";
import { ArrowRightLongIcon } from "../icons/ArrowRightLongIcon";
import { memo, useMemo, useEffect } from "react";
import { useHandleCTAClick } from "../../hooks/useHandleCTAClick";
import { isSafari } from "react-device-detect";
import { STREAM_LINK_SMALL_STYLES, STREAM_LINK_MEDIUM_STYLES, StreamLinkStyles } from "./constants/streamLinkStyles";

interface StreamLinkCardProps {
  card: ContentSectionCustomCard;
  link?: string;
  size: "s" | "m";
}

export const StreamLinkCard = memo(({ card, link, size }: StreamLinkCardProps) => {
  const handleCTAClick = useHandleCTAClick(link ?? "");
  const { isSidebarOpen } = useIsSidebarOpen();
  const breakpoint = useBreakpoint({ ssr: false });
  const { left_text, right_text, left_element, right_element, images } = card;

  const leftImage = useMemo(() =>
    images.find((i) => i.image_type_id === 17)?.medium_url
  , [images]);
  const rightImage = useMemo(() =>
    images.find((i) => i.image_type_id === 14)?.medium_url
  , [images]);

  // Update CSS variable for sidebar offset (more efficient than recalculating in JS)
  useEffect(() => {
    const offset = isSidebarOpen ? DEFAULT_SIDEBAR_WIDTH : 0;
    document.documentElement.style.setProperty('--sidebar-offset', `${offset}px`);
  }, [isSidebarOpen]);

  const styles = useMemo((): StreamLinkStyles => {
    if (size === "s") {
      return STREAM_LINK_SMALL_STYLES;
    }

    return STREAM_LINK_MEDIUM_STYLES[breakpoint] ?? STREAM_LINK_MEDIUM_STYLES.base;
  }, [size, breakpoint]);

  const isStreamLink = size === "s";

  const isSmallBreakpoint = breakpoint === "xs" || breakpoint === "base";

  const textWithCta = left_element === "text" && right_element === "cta";
  const textWithImage = left_element === "text" && right_element === "image";
  return (
    <Flex
      cursor="pointer"
      onClick={handleCTAClick}
      display="flex"
      bgColor="black"
      height={styles.height}
      width="100%"
      gap={textWithCta ? styles.gap : undefined}
      borderRadius={styles.borderRadius}
      overflow="hidden"
      align={!isStreamLink ? "center" : undefined}
      justify="space-between"
      borderBottom="1px solid"
      borderBottomColor="transparent.white.20"
    >
      {left_element && (
        <StreamLinkElement
          elementType={left_element}
          text={left_text}
          imageUrl={leftImage}
          onCta={handleCTAClick}
          smallCta={isSmallBreakpoint ? !isStreamLink : styles.smallCta}
          padding={styles.paddingX}
          textSize={styles.textSize}
          textColor={!isStreamLink ? "dune.dune" : "neutral.100"}
          fontFamily={!isStreamLink ? "Bricolage Grotesque" : undefined}
          fontWeight={!isStreamLink ? "600" : undefined}
          textWidth={isStreamLink ? undefined : "70%"}
          buttonTextSize={styles.buttonSize}
          width={
            isStreamLink || (isSafari && !styles.autoWidth) ? "auto" : undefined
          }
        />
      )}
      {right_element && (
        <StreamLinkElement
          elementType={
            isSmallBreakpoint && !isStreamLink ? "cta" : right_element
          }
          text={right_text}
          imageUrl={rightImage}
          onCta={handleCTAClick}
          smallCta={
            isSmallBreakpoint
              ? !isStreamLink
              : styles.smallCta && !textWithImage
          }
          textSize={styles.textSize}
          padding={styles.paddingX}
          textColor={!isStreamLink ? "dune.dune" : "neutral.100"}
          fontFamily={!isStreamLink ? "Bricolage Grotesque" : undefined}
          fontWeight={!isStreamLink ? "600" : undefined}
          buttonTextSize={styles.buttonSize}
          width={
            isStreamLink || (isSafari && !styles.autoWidth) ? "auto" : undefined
          }
        />
      )}
    </Flex>
  );
}, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  // Note: Removed card.images comparison as array reference comparison always fails
  // Images are memoized internally via useMemo, so changes will only occur with new card ID
  return (
    prevProps.card.id === nextProps.card.id &&
    prevProps.link === nextProps.link &&
    prevProps.size === nextProps.size
  );
});

export interface StreamLinkElementProps {
  elementType: "cta" | "text" | "image";
  text?: string;
  textSize?: string;
  textColor?: string;
  fontFamily?: string;
  fontWeight?: string;
  imageUrl?: string;
  onCta?: () => void;
  smallCta?: boolean;
  padding?: string;
  buttonTextSize?: string;
  textWidth?: string;
  width?: string;
}

export function StreamLinkElement({
  elementType,
  text,
  imageUrl,
  onCta,
  smallCta = false,
  padding,
  textSize = "16px",
  textColor = "neutral.100",
  fontFamily = "Bricolage Grotesque",
  fontWeight = "bold",
  buttonTextSize = "13px",
  textWidth,
  width,
}: StreamLinkElementProps) {
  switch (elementType) {
    case "text":
      return (
        <Flex
          height="100%"
          width={textWidth}
          align="center"
          paddingLeft={padding}
        >
          <Text
            color={textColor}
            fontSize={textSize}
            fontFamily={fontFamily}
            fontWeight={fontWeight}
            lineHeight="130%"
            noOfLines={2}
            className="multiline-clamp"
          >
            {text}
          </Text>
        </Flex>
      );

    case "image":
      return (
        <Box height={smallCta ? undefined : "inherit"}>
          <Image
            src={imageUrl}
            fit="fill"
            width={width ?? "100%"}
            height="100%"
          />
        </Box>
      );

    case "cta":
      return smallCta ? (
        <Button variant="iconOnly" onClick={onCta} paddingRight="16px">
          <ArrowRightLongIcon boxSize="24px" />
        </Button>
      ) : (
        <Flex
          height="100%"
          align="center"
          justify="center"
          paddingRight={padding}
        >
          <Button variant="primary" onClick={onCta} fontSize={buttonTextSize}>
            {text}
          </Button>
        </Flex>
      );
  }
}
